import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type {
  RegistroCapacitacion,
  AreaCapacitacion,
  TipoCapacitacion,
  EstadoCapacitacion,
} from './models/capacitacion.model';

const STORAGE_KEY = 'capacitacion_registros';

const AREAS: AreaCapacitacion[] = [
  { id: '1', nombre: 'Producción' },
  { id: '2', nombre: 'Mantenimiento' },
  { id: '3', nombre: 'Almacén' },
  { id: '4', nombre: 'Oficinas' },
  { id: '5', nombre: 'Laboratorio' },
];

function demoRegistros(): RegistroCapacitacion[] {
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const next = (daysFromNow: number) =>
    new Date(Date.now() + daysFromNow * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  return [
    {
      id: '1',
      nombreCurso: 'Inducción SST general',
      tipo: 'induccion',
      areaId: '1',
      fechaRealizacion: d(10),
      duracionHoras: 4,
      instructor: 'Ing. Laura Méndez',
      cantidadParticipantes: 12,
      estado: 'realizado',
      observaciones: 'Incluyó práctica de uso de EPP.',
      fechaProximaRecertificacion: next(355),
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      nombreCurso: 'Manejo seguro de sustancias químicas',
      tipo: 'curso',
      areaId: '5',
      fechaRealizacion: d(25),
      duracionHoras: 8,
      instructor: 'Dr. Carlos Ruiz',
      cantidadParticipantes: 8,
      estado: 'realizado',
      observaciones: 'Certificación anual.',
      fechaProximaRecertificacion: next(340),
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '3',
      nombreCurso: 'Prevención de incendios y evacuación',
      tipo: 'taller',
      areaId: '1',
      fechaRealizacion: d(60),
      duracionHoras: 2,
      instructor: 'Bomberos Voluntarios',
      cantidadParticipantes: 45,
      estado: 'realizado',
      observaciones: 'Simulacro incluido.',
      fechaProximaRecertificacion: next(305),
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '4',
      nombreCurso: 'Evaluación de riesgos por área',
      tipo: 'evaluacion',
      areaId: '2',
      fechaRealizacion: d(5),
      duracionHoras: 1,
      instructor: 'SST Interno',
      cantidadParticipantes: 15,
      estado: 'realizado',
      observaciones: 'Todos aprobados.',
      fechaProximaRecertificacion: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '5',
      nombreCurso: 'Recertificación trabajo en alturas',
      tipo: 'recertificacion',
      areaId: '2',
      fechaRealizacion: next(14),
      duracionHoras: 6,
      instructor: 'Centro de Alturas SA',
      cantidadParticipantes: 6,
      estado: 'programado',
      observaciones: 'Pendiente confirmación de lugar.',
      fechaProximaRecertificacion: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '6',
      nombreCurso: 'Ergonomía en oficina',
      tipo: 'curso',
      areaId: '4',
      fechaRealizacion: d(90),
      duracionHoras: 3,
      instructor: 'Fisioterapeuta María Soto',
      cantidadParticipantes: 22,
      estado: 'realizado',
      observaciones: 'Material entregado a cada participante.',
      fechaProximaRecertificacion: next(275),
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '7',
      nombreCurso: 'Manejo de cargas y almacén',
      tipo: 'induccion',
      areaId: '3',
      fechaRealizacion: d(120),
      duracionHoras: 2,
      instructor: 'Ing. Laura Méndez',
      cantidadParticipantes: 5,
      estado: 'realizado',
      observaciones: '',
      fechaProximaRecertificacion: '',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class CapacitacionService {
  private items: RegistroCapacitacion[] = demoRegistros();

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const data = await this.demoStorage.get<RegistroCapacitacion[]>(STORAGE_KEY);
    this.items = Array.isArray(data) ? data : demoRegistros();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return this.demoStorage.set(STORAGE_KEY, this.items).catch(() => {});
  }

  getAreas(): AreaCapacitacion[] {
    return [...AREAS];
  }

  getList(filters?: {
    busqueda?: string;
    areaId?: string;
    tipo?: TipoCapacitacion;
    estado?: EstadoCapacitacion;
  }): RegistroCapacitacion[] {
    let list = [...this.items];
    if (filters?.busqueda?.trim()) {
      const q = filters.busqueda.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.nombreCurso.toLowerCase().includes(q) ||
          r.instructor.toLowerCase().includes(q) ||
          r.observaciones.toLowerCase().includes(q)
      );
    }
    if (filters?.areaId) list = list.filter((r) => r.areaId === filters.areaId);
    if (filters?.tipo) list = list.filter((r) => r.tipo === filters.tipo);
    if (filters?.estado) list = list.filter((r) => r.estado === filters.estado);
    return list.sort((a, b) => b.fechaRealizacion.localeCompare(a.fechaRealizacion));
  }

  getById(id: string): RegistroCapacitacion | null {
    return this.items.find((r) => r.id === id) ?? null;
  }

  create(dto: Omit<RegistroCapacitacion, 'id' | 'createdAt' | 'updatedAt'>): RegistroCapacitacion {
    const id = String(Date.now());
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: RegistroCapacitacion = {
      ...dto,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.items.push(nuevo);
    this.persistDemo();
    return nuevo;
  }

  update(
    id: string,
    dto: Partial<Omit<RegistroCapacitacion, 'id' | 'createdAt'>>
  ): RegistroCapacitacion | null {
    const idx = this.items.findIndex((r) => r.id === id);
    if (idx === -1) return null;
    const updated: RegistroCapacitacion = {
      ...this.items[idx],
      ...dto,
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
    };
    this.items[idx] = updated;
    this.persistDemo();
    return updated;
  }

  delete(id: string): boolean {
    const idx = this.items.findIndex((r) => r.id === id);
    if (idx === -1) return false;
    this.items.splice(idx, 1);
    this.persistDemo();
    return true;
  }
}
